import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass } from '@/components/ui/inputStyles';

export type PromotionAreaScopeItem = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
  owner_username?: string | null;
};

type ListResp = {
  ok: boolean;
  items: PromotionAreaScopeItem[];
  total: number;
  page: number;
  per_page: number;
};

const PER_PAGE = 30;

type Props = {
  /** 当前页 view：config | entries */
  currentView: 'config' | 'entries';
  className?: string;
};

/**
 * 参数配置 / 推广信息管理页顶部：快速切换推广区域。
 */
export function PromotionAreaScopeBar({ currentView, className }: Props) {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const currentId = Math.max(0, parseInt(idParam || '0', 10) || 0);
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState<PromotionAreaScopeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<ListResp>('list_promotion_areas', {
        method: 'GET',
        query: { page, per_page: PER_PAGE },
      });
      if (r.ok) {
        setItems(r.items);
        setTotal(r.total);
      } else {
        setItems([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    const q = appliedQ.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) => {
      const hay = `${a.name} ${a.public_id} #${a.id} ${a.owner_username ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, appliedQ]);

  const selected = useMemo(
    () => items.find((a) => a.id === currentId) ?? filteredItems.find((a) => a.id === currentId),
    [items, filteredItems, currentId],
  );

  function goArea(areaId: number) {
    if (areaId < 1) return;
    const sp = new URLSearchParams(searchParams);
    sp.set('view', currentView);
    navigate(`/promotion-areas/${areaId}?${sp.toString()}`);
  }

  function runSearch() {
    setAppliedQ(keyword.trim());
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <Card title="切换推广区域" className={className}>
      {loading ? (
        <Spinner label="加载区域列表" />
      ) : items.length === 0 ? (
        <p className="text-sm text-amber-800">暂无推广区域，请先在列表页新建。</p>
      ) : (
        <>
          <div className="mb-3 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
            <label className={`${labelClass} min-w-[14rem] flex-1`}>
              <span>推广区域</span>
              <select
                className={inputClass}
                value={currentId}
                onChange={(e) => goArea(Math.max(0, parseInt(e.target.value, 10) || 0))}
              >
                <option value={0} disabled>
                  请选择
                </option>
                {selected && !filteredItems.some((a) => a.id === selected.id) ? (
                  <option value={selected.id}>
                    {selected.name}
                    {!selected.enabled ? '（已停用）' : ''} · #{selected.id}
                  </option>
                ) : null}
                {filteredItems.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {!a.enabled ? '（已停用）' : ''} · {a.public_id}
                  </option>
                ))}
              </select>
            </label>
            <label className={`${labelClass} min-w-[10rem] flex-1`}>
              <span>筛选名称 / 编号</span>
              <input
                type="search"
                className={inputClass}
                placeholder="本页内筛选"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
              />
            </label>
            <Button type="button" variant="secondary" onClick={runSearch}>
              查询
            </Button>
          </div>
          {total > PER_PAGE ? (
            <PaginationBar
              className="border-t border-slate-100 pt-3"
              page={page}
              totalPages={totalPages}
              total={total}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          ) : null}
        </>
      )}
    </Card>
  );
}
